import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft, AlertCircle } from 'lucide-react';
import { useAuth } from '@contexts/AuthContext';
import logoWordmark from '../assets/logo-glk-wordmark.png';

export function Login() {
  const { login } = useAuth();
  const navigate  = useNavigate();
  const [form, setForm]       = useState({ email: '', password: '' });
  const [showPass, setShow]   = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const result = await login(form.email, form.password);
    setLoading(false);
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gl-void px-4"
      style={{
        backgroundImage: `
          radial-gradient(ellipse at 30% 60%, rgba(45,125,70,0.05) 0%, transparent 50%),
          radial-gradient(ellipse at 70% 40%, rgba(26,94,158,0.04) 0%, transparent 50%)
        `,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
        className="w-full max-w-sm"
      >
        {/* Back link */}
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-gl-muted hover:text-gl-ghost mb-8 transition-colors">
          <ArrowLeft size={12} />
          Back to hub
        </Link>

        {/* Card */}
        <div
          className="rounded-2xl p-8"
          style={{
            background:     'rgba(10,16,35,0.9)',
            border:         '1px solid rgba(255,255,255,0.07)',
            backdropFilter: 'blur(24px)',
            boxShadow:      '0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(45,125,70,0.06)',
          }}
        >
          {/* Logo + title */}
          <div className="text-center mb-8">
            <img
              src={logoWordmark}
              alt="GroundLink III Handling"
              className="h-16 w-auto mx-auto mb-4"
            />
            <h1 className="text-xl font-display font-semibold text-gl-ghost">Sign in</h1>
            <p className="text-xs text-gl-muted mt-1">GroundLink Handling Intranet</p>
          </div>

          {/* Error */}
          <AnimatePresence>
            {error && (
              <motion.div
                role="alert"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="flex items-center gap-2 p-3 rounded-lg mb-5 text-xs"
                style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#EF4444' }}
              >
                <AlertCircle size={12} className="shrink-0" />
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-2xs font-mono uppercase tracking-widest text-gl-muted mb-1.5">
                Email
              </label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@groundlinkhandling.pt"
                className="w-full px-4 py-3 rounded-xl text-sm text-gl-ghost placeholder-gl-muted bg-gl-surface border border-gl-border focus:outline-none transition-all"
                style={{ '--tw-ring-color': '#2D7D46' }}
                onFocus={(e) => (e.target.style.borderColor = 'rgba(45,125,70,0.4)')}
                onBlur={(e)  => (e.target.style.borderColor = '')}
              />
            </div>

            <div>
              <label className="block text-2xs font-mono uppercase tracking-widest text-gl-muted mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  required
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 pr-11 rounded-xl text-sm text-gl-ghost placeholder-gl-muted bg-gl-surface border border-gl-border focus:outline-none transition-all"
                  onFocus={(e) => (e.target.style.borderColor = 'rgba(45,125,70,0.4)')}
                  onBlur={(e)  => (e.target.style.borderColor = '')}
                />
                <button
                  type="button"
                  onClick={() => setShow(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gl-muted hover:text-gl-ghost transition-colors"
                >
                  {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            <motion.button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl text-sm font-display font-semibold mt-2 transition-all"
              style={{
                background: loading ? 'rgba(45,125,70,0.4)' : 'linear-gradient(135deg, #2D7D46, #1A5E9E)',
                color: '#0D1B2A',
                boxShadow: loading ? 'none' : '0 0 30px rgba(45,125,70,0.25)',
              }}
              whileHover={{ scale: loading ? 1 : 1.02 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
                  Authenticating...
                </span>
              ) : (
                'Sign In'
              )}
            </motion.button>
          </form>

          <p className="text-center text-2xs text-gl-muted mt-6">
            Trouble signing in? Contact{' '}
            <a href="mailto:it@groundlinkhandling.pt" className="underline hover:text-gl-ghost transition-colors">
              IT Support
            </a>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
